import React, { useEffect, useState, useRef, useMemo } from "react";
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getBookingDoctors } from './Services/bookingDoctorsService';
import { getBookingPatientLists } from './Services/bookingDoctorsService';
import { serverConfig } from "../ApiSearchPath/ApiSearchPath";
import { saveBooking } from "./Services/BookingService";
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';
import PatientService from "./Services/PatientService";
import Swal from "sweetalert2";
import { CreateInvoiceService } from "./Services/CreateInvoiceService";

function useQueryParams() {
  return new URLSearchParams(useLocation().search);
}
export default function Booking() {
   const { pathnames } = useLocation();
     useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathnames]);
  const updatedBy = localStorage.getItem('userId');
  const through = localStorage.getItem('userType');
  useEffect(() => {
    const scriptUrls = [
      "assets/js/jquery-3.7.1.min.js",
      "assets/js/bootstrap.bundle.min.js",
      "assets/js/feather.min.js",
      "assets/js/moment.min.js",
      "assets/js/bootstrap-datetimepicker.min.js",
      "assets/plugins/select2/js/select2.min.js",
      "assets/js/script.js",
    ];
    let isCancelled = false;

    const loadScriptsSequentially = async () => {
      for (const url of scriptUrls) {
        if (isCancelled) break;

        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = url;
          script.async = false;
          script.onload = () => {
            console.log(`Loaded: ${url}`);
            resolve();
          };
          script.onerror = () => {
            console.error(`Failed to load: ${url}`);
            reject();
          };
          document.body.appendChild(script);
        });
      }

      // Now init plugins after scripts are loaded
      if (!isCancelled) {
        // if (window.AOS) {
        //   window.AOS.init();
        // }

        if (window.$ && typeof window.$.fn.datetimepicker === 'function') {
          window.$('.datetimepicker').datetimepicker();
        }

        if (typeof window.initBackToTop === 'function') {
          window.initBackToTop(); // assuming your backToTop script defines this
        }
        console.log('All scripts loaded and initialized.');
      }
    };
    loadScriptsSequentially();
    return () => {
      isCancelled = true;
    };
  }, []);
  const query = useQueryParams();
  const doctorId = query.get('doctorId');

  const { data, isLoading, error } = useQuery({
    queryKey: ['bookingDoctor', doctorId],
    queryFn: () => getBookingDoctors(doctorId),
    enabled: !!doctorId, // only run if doctorId is available
  });
  const [showForm, setShowForm] = useState(false);
  const [isAddNew, setIsAddNew] = useState(false);
  const {
    data: rawPatientData,
    isLoading: isLoadingPat
  } = useQuery({
    queryKey: ['companyPatients', data?.companyId],
    queryFn: () => getBookingPatientLists(updatedBy, data?.companyId),
    enabled: !!data?.companyId,
  });

  // ✅ Handle wrapped API response (common issue)
  const patientData = Array.isArray(rawPatientData)
    ? rawPatientData
    : rawPatientData?.data?.data?.flat() || [];

  const [selectPatientFromList, setSelectPatientFromList] = useState();
  const [currentStep, setCurrentStep] = useState(1); // Step 1 = Specialty

  const nextStep = (e, no) => {
    e.preventDefault();
    setCurrentStep(no + 1);
    // Prevents going past step 6
  };

  const prevStep = (e, no) => {
    e.preventDefault();
    setCurrentStep(no - 1);
    // Prevents going below step 1
  };
  const pickerRef = useRef(null);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const formatToISO = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    // Using 12:00:00 as a safe default time to prevent day-flipping
    console.log(`${year}-${month}-${day}T12:00:00`);
    return `${year}-${month}-${day}T12:00:00`;
  };
  // Desktop calendar
  useEffect(() => {
    if (currentStep !== 1 || !pickerRef.current) return;
    const today = new Date();

    const fp = flatpickr(pickerRef.current, {
      inline: true,
      dateFormat: 'd-m-Y',
      minDate: 'today',
      defaultDate: today,
      position: "auto",

      onReady: (selectedDates) => {
        const date = selectedDates[0] || today;
        setSelectedDate(date);
        // FIX: Store as string immediately
        setAppointmentDateInput(formatToISO(date));
      },

      onChange: (selectedDates) => {
        if (selectedDates.length > 0) {
          const date = selectedDates[0];
          setSelectedDate(date);
          // FIX: Store as string immediately
          setAppointmentDateInput(formatToISO(date));
          setAppointmentTimeInput('');
        }
      },
    });

    return () => {
      fp.destroy();
    };
  }, [currentStep, isLoading]);

  const [mobileDates, setMobileDates] = useState([]);

  useEffect(() => {
    // Generate next 14 days for the mobile scroller
    const dates = [];
    for (let i = 0; i < 30; i++) {
      const d = new Date();
      d.setDate(d.getDate() + i);
      dates.push(d);
    }
    setMobileDates(dates);
  }, []);

  const timeSlots = useMemo(() => {
    if (!data?.availableTtResp || !selectedDate) return { morning: [], afternoon: [], evening: [] };

    const parseTime = (baseDate, timeStr) => {
      const [h, m, s] = timeStr.split(':');
      const date = new Date(baseDate);
      date.setHours(+h, +m, +s || 0, 0);
      return date;
    };

    const generateTimeSlots = (start, end) => {
      const slots = [];
      let current = new Date(start);

      while (current.getTime() + 15 * 60 * 1000 <= end.getTime()) {
        slots.push(new Date(current));
        current = new Date(current.getTime() + 15 * 60 * 1000);
      }

      // ✅ Add final short slot if there's leftover time
      if (current < end) {
        slots.push(new Date(current)); // final short slot
      }

      return slots;
    };


    const dayName = selectedDate.toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase();

    const daySlots = data.availableTtResp
      .filter(a => a.isOpen === 'Y' && a.day?.trim().toUpperCase() === dayName)
      .map(a => ({
        start: parseTime(selectedDate, a.openingTime),
        end: parseTime(selectedDate, a.closingTime),
      }))
      .sort((a, b) => a.start - b.start);

    // ✅ Merge overlapping time ranges
    const mergedRanges = [];
    for (const slot of daySlots) {
      const last = mergedRanges[mergedRanges.length - 1];
      if (last && slot.start <= last.end) {
        last.end = new Date(Math.max(last.end.getTime(), slot.end.getTime()));
      } else {
        mergedRanges.push({ ...slot });
      }
    }

    const slotSet = new Set();
    const morning = [], afternoon = [], evening = [];

    for (const range of mergedRanges) {
      const slots = generateTimeSlots(range.start, range.end);
      for (const time of slots) {
        const key = time.toISOString();
        if (slotSet.has(key)) continue;
        slotSet.add(key);

        const hour = time.getHours();
        if (hour < 12) morning.push(time);
        else if (hour < 17) afternoon.push(time);
        else evening.push(time);
      }
    }

    return { morning, afternoon, evening };
  }, [data, selectedDate]);

  const renderSlot = (time, nextTime) => {
    const start = time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const end = nextTime
      ? nextTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : null;
    const now = new Date(); // current time
    const isPast = time < now;
    const handleSelect = () => {
      setAppointmentTimeInput(`${start} - ${end}`);
    };
    return (
      <div className="form-check-inline visits me-0" key={time.toISOString()}>
        <label className="visit-btns">
          <input type="radio" className="form-check-input"
            name="timeSlot"
            onChange={handleSelect}
            disabled={isPast} />
          <span className="visit-rsn" style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '8px 12px',

            borderRadius: '6px',
            minWidth: '140px', // ✅ forces consistent width
            textAlign: 'center',
            whiteSpace: 'nowrap',
          }}>
            {start}&nbsp;&nbsp;{end && ` - ${end}`}
          </span>
        </label>
      </div>
    );
  };

  const [genderInput, setGenderInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [ageInput, setAgeInput] = useState('');
  const [dob, setDob] = useState('');
  const [address, setAddress] = useState('');
  const [pincode, setPincode] = useState('');
  const [city, setCity] = useState('');

  // Appointment State Variables
  const [appointmentDateInput, setAppointmentDateInput] = useState('');
  const [appointmentTimeInput, setAppointmentTimeInput] = useState('');
  const [appointmentTypeInput, setAppointmentTypeInput] = useState('clinic');
  const [problemInput, setProblemInput] = useState('');

  // Payment State Variables
  const paymentTypeInput = 'ONCASH';
  const statusPaymentInput = 'UNPAID';
  //Prescription State Variables

  //History State Variables
  const [bookingId, setBookingId] = useState(null);
  const [patientNumber, setPatientNumber] = useState(null);
  const [patientId, setPatientId] = useState(null);
  const [patientOptions, setPatientOptions] = useState([]);
  const [isPatientDropdownOpen, setIsPatientDropdownOpen] = useState(false);
  const [patientName, setPatientName] = useState("");
  const [phone, setPhone] = useState();
  const [visitType, setVisitType] = useState("New");
  // Search customers
  // Search Patients by Phone/SearchTerm

  const [testRows, setTestRows] = useState([]);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    let newErrors = {};
    let isValid = true;

    if (!appointmentTypeInput) {
       newErrors.appointmentTypeInput = "Please select the appointment type.";
       isValid = false;
    }
    if (!appointmentDateInput || !appointmentTimeInput) {
       newErrors.appointmentDateTime = "Please select the date and time.";
       isValid = false;
    }
    
    if (showForm) {
      if (!phone || !/^\d{10}$/.test(phone)) {
        newErrors.phone = "Enter a valid 10-digit phone number";
        isValid = false;
      }
      if (!patientName || !/^[A-Za-z\s]{1,30}$/.test(patientName)) {
        newErrors.patientName = "Name must be max 30 characters and alphabets only";
        isValid = false;
      }
      if (!genderInput || genderInput === "--select--") {
        newErrors.genderInput = "Please select gender";
        isValid = false;
      }
      if (!ageInput) {
        newErrors.ageInput = "Age is required";
        isValid = false;
      }
      if (!address || address.trim() === '') {
        newErrors.address = "Address is required";
        isValid = false;
      }
      if (!problemInput || problemInput.trim() === '') {
        newErrors.problemInput = "Reason for visit is required";
        isValid = false;
      }
      // ✅ NEW: Inline Email Validation (Optional field)
    if (emailInput && emailInput.trim() !== '') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailInput)) {
        newErrors.emailInput = "Please enter a valid email address";
        isValid = false;
      }
    }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleBookingSubmit = async (e) => {

    e.preventDefault();

    // Use local variables instead of useState setters

    const specialities = data?.specialities;
    let paymentAmount = Number(data?.consultaionFee);
    if (visitType === "FollowUp") {
      paymentAmount = 0;
    }
    const companyId = data?.companyId;
    const clinicName = data?.clinicName;
    const status = 'BOOKED';
    const throughInput = "PATIENT";
    const appointmentDate = new Date(selectedDate); // Convert string to Date
    appointmentDate.setDate(appointmentDate.getDate() + 15); // Add 15 days

    //const validUntil = appointmentDate.toISOString().split('T')[0]; 

    const userId = data?.doctorId;
    const prescriptionUserId = data?.doctorId;
    const prescriptionCompanyId = data?.companyId;
    const historyUserId = data?.doctorId;
    const historyCompanyId = data?.companyId;

    const bookingRequest = {
      patient: {
        patientId: patientId,
        patientName: patientName,
        gender: genderInput,
        phone: phone,
        age: ageInput,
        email: emailInput,
        dob: dob,
        city: city,
        pincode: pincode,
        address: address,
        createdBy: updatedBy,
        updatedBy: updatedBy,
        through: through
      },
      appointment: {
        appointmentDate: appointmentDateInput,
        appointmentTime: appointmentTimeInput,
        appointmentType: appointmentTypeInput,
        appointmentMode: 'ONLINE',
        specialities,
        problem: problemInput,
        through: through,
        status,
        visitType: visitType,
        companyId,
        companyName: clinicName,
        doctorId: userId,
        createdBy: updatedBy,
        updatedBy: updatedBy
      },
      payment: {
        paymentAmount,
        paymentType: paymentTypeInput,
        paymentReference: "Onclinic",
        status: statusPaymentInput,
        through: through,
        userId,
        companyId
      },
      prescription: {
        through: throughInput,
        userId: prescriptionUserId,
        companyId: prescriptionCompanyId,
      },
      history: {
        userId: historyUserId,
        companyId: historyCompanyId,
        through: through
      },
    };


    try {
      const res = await saveBooking(bookingRequest);
      const bookId = res.bookId;
      const patientNo = res.patientNo;
      setBookingId(bookId);
      setPatientNumber(patientNo);
      console.log(res);
      const patientIdTemp = res.patientId;
      const appointmentIdTemp = res.appointmentId;
      const newRow = {
        id: 0,
        test: "Consultaion",
        notes: "Consultation Fee",
        price: paymentAmount,
        quantity: 1,
        totalPrice: paymentAmount,
        testId: appointmentIdTemp,
        testType: "Appointment"
      };
      const updatedRows = [...testRows, newRow];
      setTestRows(updatedRows);
      const inv = {
        patientId: patientIdTemp,
        reportingDate: appointmentDateInput,
        reportingTime: appointmentTimeInput,
        testRows: updatedRows,
        discount: (0).toFixed(2),
        discountBy: "No One",
        paidAmount: (statusPaymentInput === "PAID") ? Number(paymentAmount).toFixed(2) : 0,
        dues: (statusPaymentInput === "PAID") ? 0 : Number(paymentAmount).toFixed(2),
        totalAmount: Number(paymentAmount).toFixed(2),
        createdBy: updatedBy,
        through: through,
        updatedBy: updatedBy,
        companyId: companyId,
        doctorId: data?.doctorId,
        invType: 'Appointment'
      };
      const response = await CreateInvoiceService.insertInvoices(inv);
      const receipt = response?.data?.data;
      console.log(receipt);
      console.log(res);
    } catch (err) {
      setCurrentStep(currentStep - 1);
      console.error('Booking failed:', err);
      Swal.fire({
        title: 'Failed!',
        text: `Error during booking details: ${(err.response?.data?.statusDescription || err.message)}`,
        icon: 'error',
        confirmButtonColor: '#566fe2', // your custom button color
        customClass: {
          title: 'swal-title-small',
          popup: 'swal-popup-small',
          confirmButton: 'swal-button-small'
        }
      });

    }
  };
  // ✅ Format the selected date right here
  const formattedDate = appointmentDateInput
    ? new Date(appointmentDateInput).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
    })
    : '';

  if (isLoading) return <div>Loading doctor details...</div>;
  if (error) return <div>Error fetching doctor details.</div>;
  const handleSearchChange = async (e) => {
    const query = e.target.value;
    setPhone(query);

    if (query.length >= 9) {
      try {
        const dataRes = await PatientService.fetchCustomers(data?.companyId, query);
        setPatientOptions(dataRes || []);
        setIsPatientDropdownOpen(dataRes && dataRes.length > 0);
      } catch (err) {
        setPatientOptions([]);
        setIsPatientDropdownOpen(false);
      }
    } else {
      setPatientOptions([]);
      setIsPatientDropdownOpen(false);
    }
  };
  const selectPayment = "ONCLINICPAYMENT";
  // Select Patient and Auto-fill
  const handleSelectPatient = async (patient) => {
    if (!doctorId) {
      Swal.fire({
        icon: "warning",
        title: "Your doctor is missing!",
        text: "Please select doctor first",
      });
      return;
    }
    const patId = patient.patientId;
    const dataVisits = await PatientService.getPatientsRefForVisits(patId, doctorId, appointmentDateInput);
    setPatientId(patient.patientId);
    setPatientName(patient.patientName);
    setPhone(patient.phone);
    setAddress(patient.address || "");
    setGenderInput(patient.gender);
    setAgeInput(patient.age);
    setEmailInput(patient.emailId);
    setDob(patient.dob);
    setCity(patient.city);
    setPincode(patient.pinCode);
    const visitTypes = dataVisits?.data?.data?.visitType;
    if (visitTypes) {
      setVisitType(visitTypes);
    }
    else {
      setVisitType("New");
    }

    setPatientOptions([]);
    setIsPatientDropdownOpen(false);
  };
  const resetForm = () => {
    setPhone("");
    setPatientName("");
    setGenderInput("--select--");
    setEmailInput("");
    setAgeInput("");
    setDob("");
    setAddress("");
    setProblemInput("");
    setPincode("");
    setCity("");
  };
  
  

  return (
    <>
      <style>{`
          .wizard-scroll {
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  -webkit-overflow-scrolling: touch; /* smooth scroll on mobile */
}
.wizard-scroll::-webkit-scrollbar {
  display: none;
}
.form-wizard-steps {
  display: flex !important;
  flex-wrap: nowrap !important;   /* override bootstrap */
  overflow-x: auto;
  overflow-y: visible;
  white-space: nowrap;
  gap: 30px;
  padding: 0 15px;
  -webkit-overflow-scrolling: touch;
}

.form-wizard-steps li {
  flex: 0 0 auto !important; /* prevents shrinking */
}

.appointment-scroll {
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  gap: 16px;
  padding: 10px 15px;
  -webkit-overflow-scrolling: touch;
}
.appointment-scroll-mobile {
  display: flex;
  flex-direction: column;
  max-height: 250px; /* Fixed height for scrolling */
  overflow-y: auto;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  padding: 10px;
  z-index: 1000;
}

/* Slot selection styling inside dropdown */
@media (max-width: 540px) {
   .card-body{
      padding-left:12px;
      padding-right:12px;
   
   }
}
@media (max-width: 767px) {
  .appointment-scroll-mobile .visits {
    width: 100% !important;
    margin: 4px 0 !important;
  }
  
  .appointment-scroll-mobile .visit-rsn {
    width: 100% !important;
    justify-content: flex-start !important;
    padding: 12px !important;
    border: 1px solid #f0f0f0 !important;
    background: #fafafa !important;
    border-radius: 6px !important;
  }

  /* Custom styling to hide the radio dot if you want a clean list */
  .appointment-scroll-mobile input[type="radio"] {
    margin-right: 10px;
  }
}
.appointment-item {
  flex: 0 0 auto;
  min-width: 140px; /* controls card width */
}
  .doctor-info {
  display: flex;
  flex-direction: column; /* IMPORTANT */
}
      `}</style>{
((data?.showIdentity === "N") || (!data?.showIdentity)
          || (!data?.showCompanyIdentity) ||
          (data?.showCompanyIdentity === "N") ||
          (data?.kycAccept !=="Y") || (!data?.kycAccept)) ? (<>
            {/* Breadcrumb still shows (looks professional) */}
            <div className="breadcrumb-bar">
              <div className="container">
                <div className="row align-items-center inner-banner">
                  <div className="col-md-12 col-12 text-center">
                    <nav aria-label="breadcrumb" className="page-breadcrumb">
                      {/* <ol className="breadcrumb">
                    <li className="breadcrumb-item"><a href="/main-page"><i className="isax isax-home-15" /></a></li>
                    <li className="breadcrumb-item" aria-current="page">Doctors</li>
                    <li className="breadcrumb-item active">Doctor Profile</li>
                  </ol> */}
                      <h2 className="breadcrumb-title">Doctor Profile</h2>
                    </nav>
                  </div>
                </div>
              </div>

            </div>

            {/* Centered Loading Processor */}
            <div className="content" >
              <div className="container">
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ minHeight: "20vh" }}
                >
                  <div className="text-center">
                    {/* Beautiful spinning loader using your existing Font Awesome */}
                    {/* <i 
                  className="fa-solid fa-spinner fa-spin fa-4x mb-4" 
                  style={{ color: "#0d6efd" }}
                /> */}{(data?.showIdentity === "N") || (!data?.showIdentity)
          || (!data?.showCompanyIdentity) ||
          (data?.showCompanyIdentity === "N")?<>
                    <h2 className="mb-2 error-title">Identity not verified!</h2>
                    <p className="text-muted">Please search to other doctor, this profile is not verified.</p></>:
                    ((data?.kycAccept !=="Y") || (!data?.kycAccept))?
                  <><h2 className="mb-2 error-title">Kyc not done!</h2>
                    <p className="text-muted">Please search to other doctor, this profile has not done kyc yet.</p></>:
                    <><h2 className="mb-2 error-title">Identity not verified!</h2>
                    <p className="text-muted">Please search to other doctor, this profile is not verified.</p></>}
                  </div>
                </div>
              </div>
            </div>
          </>) : (
      <div>
        {/* Header */}
        <header className="header header-custom header-fixed inner-header relative">
          <div className="container">
            <nav className="navbar navbar-expand-lg header-nav">
              <div className="navbar-header">
                <a id="mobile_btn" href="/" role="button" onClick={(e) => { e.preventDefault() }}>
                  <span className="bar-icon">
                    <span />
                    <span />
                    <span />
                  </span>
                </a>
                <a href="/" role="button" onClick={(e) => { e.preventDefault() }} className="navbar-brand logo">
                  <img src="assets/img/logo.svg" className="img-fluid" alt="Logo" />
                </a>
              </div>
            </nav>
          </div>
        </header>
        {/* /Header */}
        {/* Terms */}

        <div className="doctor-content">
          <div className="container">
            <div className="row">
              <div className="col-lg-10 mx-auto">
                <div className="booking-wizard">
                  <div className="wizard-scroll">
                    <ul className="form-wizard-steps d-flex align-items-center justify-content-sm-center" id="progressbar2">
                      <li className={currentStep === 1 ? 'progress-active' : ''}>
                        <div className="profile-step">
                          <span className="multi-steps">1</span>
                          <div className="step-section">
                            <h6>Basic Details</h6>
                          </div>
                        </div>
                      </li>
                      {/* <li className={currentStep === 2 ? 'progress-active' : ''}>
                <div className="profile-step">
                  <span className="multi-steps">2</span>
                  <div className="step-section">
                    <h6>Appointment Type</h6>
                  </div>
                </div>
              </li> */}
                      {/* <li className={currentStep === 3 ? 'progress-active' : ''}>
                <div className="profile-step">
                  <span className="multi-steps">3</span>
                  <div className="step-section">
                    <h6>Date &amp; Time</h6>
                  </div>
                </div>
              </li> */}
                      {/* <li className={currentStep === 4 ? 'progress-active' : ''}>
                <div className="profile-step">
                  <span className="multi-steps">4</span>
                  <div className="step-section">
                    <h6>Basic Information</h6>
                  </div>
                </div>
              </li> */}
                      <li className={currentStep === 2 ? 'progress-active' : ''}>
                        <div className="profile-step">
                          <span className="multi-steps">2</span>
                          <div className="step-section">
                            <h6>Payment</h6>
                          </div>
                        </div>
                      </li>
                      <li className={currentStep === 3 ? 'progress-active' : ''}>
                        <div className="profile-step">
                          <span className="multi-steps">3</span>
                          <div className="step-section">
                            <h6>Confirmation</h6>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="booking-widget multistep-form mb-5">
                  {currentStep === -1 && (
                    <div className="card booking-card mb-0">
                      <div className="card-header">
                        <div className="booking-header pb-0">
                          <div className="card mb-0">
                            <div className="card-body">
                              <div className="d-flex align-items-center flex-wrap rpw-gap-2 flex-wrap row-gap-2">
                                <span className="avatar avatar-xxxl avatar-rounded me-2 flex-shrink-0"><img src={`${serverConfig.BASE_URL}/uploads/${data.image}`} alt={"appointment"} onError={(e) => {
                                  e.target.onerror = null; // prevents infinite loop
                                  e.target.src = "assets/img/doctor.png";
                                }} /></span>
                                <div>
                                  <h4 className="mb-1">Dr. {data.doctorName} </h4>
                                  <p className="text-indigo mb-3 fw-medium">{data.designation}</p>
                                  <p className="mb-0"><i className="isax isax-location me-2"/>{data.location}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="card-body booking-body">
                        <div className="card mb-0">
                          <div className="card-body pb-1">
                            <div className="mb-4 pb-4 border-bottom">
                              <label className="form-label">Speciality</label>
                              <div className="position-relative">
                                <select
                                  className="form-control pe-5"
                                  disabled
                                  style={{
                                    appearance: 'none',
                                    WebkitAppearance: 'none',
                                    MozAppearance: 'none',
                                    backgroundColor: '#e9ecef', // matches Bootstrap's disabled input
                                    border: '1px solid #ced4da',
                                    borderRadius: '0.375rem',
                                    cursor: 'not-allowed',
                                  }}
                                >
                                  <option>{data.specialities}</option>
                                </select>
                                <i
                                  className="fas fa-chevron-down position-absolute"
                                  style={{
                                    top: '50%',
                                    right: '15px',
                                    transform: 'translateY(-50%)',
                                    pointerEvents: 'none',
                                    color: '#6c757d',
                                  }}
                                ></i>
                              </div>
                            </div>
                            <h6 className="mb-3">Pricing</h6>
                            <div className="row">
                              <div className="col-lg-4 col-md-6">
                                <div className="service-item active">
                                  <input className="form-check-input ms-0 mt-0" name="service1" type="checkbox" id="service1" defaultChecked />
                                  <label className="form-check-label ms-2" htmlFor="service1">
                                    <span className="fs-14 d-block">&#8377; {data.consultaionFee}</span>
                                  </label>
                                </div>
                              </div>

                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="card-footer mt-4">
                        <div className="d-flex align-items-center justify-content-between">
                          {currentStep > 0 && (
                            <button className="btn btn-md btn-dark rounded-pill" onClick={(e) => { prevStep(e, 2) }}>
                              <i className="isax isax-arrow-left-2 me-1" />
                              Back
                            </button>
                          )}
                          {currentStep < 1 && (
                            <button className="btn btn-md btn-primary-gradient rounded-pill" onClick={(e) => { nextStep(e, 1) }}>
                              Select Appointment Type
                              <i className="isax isax-arrow-right-3 ms-1" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                  )}
                  {currentStep === 1 && (
                    <div className="card booking-card mb-0">
                      <div className="card-header">
                        <div className="booking-header pb-0">
                          <div className="card mb-0">
                            <div className="card-body">
                              <div className="d-flex align-items-center flex-wrap rpw-gap-2 flex-wrap row-gap-2">
                                <span className="avatar avatar-xxxl avatar-rounded me-2 flex-shrink-0"><img src={`${serverConfig.BASE_URL}/uploads/${data.image}`} alt={"appointment"} onError={(e) => {
                                  e.target.onerror = null; // prevents infinite loop
                                  e.target.src = "assets/img/doctor.png";
                                }} /></span>
                                <div>
                                  <h4 className="mb-1">Dr. {data.doctorName} </h4>
                                  <p className="text-indigo mb-3 fw-medium">{data.designation}</p>
                                  <p className="mb-0"><i className="isax isax-location me-2" />{data.location}</p>
                                </div>
                              </div>
                              <div className="row">
                                <h6 class="mt-1 ml-0" style={{ paddingLeft: "10px" }}>Booking Info</h6>
                                <div class="row gx-1 gy-1">
                                  <div class="col-lg-3 col-sm-6" >
                                    <div style={{ paddingLeft: "10px" }}>
                                      <h6 class="fs-14 fw-medium mb-1">Specialities</h6>
                                      <p class="mb-0">{data.specialities}</p>
                                    </div>
                                  </div>
                                  <div class="col-lg-3 col-sm-6">
                                    <div style={{ paddingLeft: "10px" }}>
                                      <h6 class="fs-14 fw-medium mb-1">Date & Time</h6>
                                      <p class="mb-0">{appointmentTimeInput && appointmentDateInput && (
                                        <>
                                          {appointmentTimeInput}, {formattedDate}
                                        </>
                                      )}</p>
                                    </div>
                                  </div>
                                  <div class="col-lg-3 col-sm-6">
                                    <div style={{ paddingLeft: "10px" }}>
                                      <h6 class="fs-14 fw-medium mb-1">Appointment type</h6>
                                      <p class="mb-0">{(appointmentTypeInput === 'clinic')?'Clinic':
                                         (appointmentTypeInput === 'videoCall')? 'Video call':
                                         (appointmentTypeInput === 'audioCall')? 'Audio call':
                                         (appointmentTypeInput === 'chat')? 'Chat':
                                         (appointmentTypeInput === 'homeVisit')? 'Home visit':
                                         appointmentTypeInput

                                      }
                                        </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="card-body booking-body">
                        <div className="card mb-0">
                          <div className="card-body pb-1">
                            <h6 className="mb-3">Select Appointment Type</h6>
                            <div className="appointment-scroll">
                              {data?.appointments?.clinic === 'Y' && (
                                <div className="appointment-item">
                                  <div className="radio-select text-center">
                                    <input className="form-check-input ms-0 mt-0" name="service2" type="radio" id="service7"
                                      value="clinic"
                                      checked={appointmentTypeInput === 'clinic'}
                                      onChange={(e) => setAppointmentTypeInput(e.target.value)} />
                                    <label className="form-check-label" htmlFor="service7">
                                      <i className="isax isax-hospital5" />
                                      <span className="service-title d-block">In-Clinic</span>
                                    </label>
                                  </div>
                                </div>)}
                              {data?.appointments?.videoCall === 'Y' && (
                                <div className="appointment-item">
                                  <div className="radio-select text-center">
                                    <input className="form-check-input ms-0 mt-0" name="service2" type="radio" id="service8"
                                      value="videoCall"
                                      checked={appointmentTypeInput === 'videoCall'}
                                      onChange={(e) => setAppointmentTypeInput(e.target.value)} />
                                    <label className="form-check-label" htmlFor="service8">
                                      <i className="isax isax-video5" />
                                      <span className="service-title d-block">Video Call</span>
                                    </label>
                                  </div>
                                </div>)}
                              {data?.appointments?.audioCall === 'Y' && (
                                <div className="appointment-item">
                                  <div className="radio-select text-center">
                                    <input className="form-check-input ms-0 mt-0" name="service2" type="radio" id="service9"
                                      value="audioCall"
                                      checked={appointmentTypeInput === 'audioCall'}
                                      onChange={(e) => setAppointmentTypeInput(e.target.value)} />
                                    <label className="form-check-label" htmlFor="service9">
                                      <i className="isax isax-call5" />
                                      <span className="service-title d-block">Audio Call</span>
                                    </label>
                                  </div>
                                </div>)}
                              {data?.appointments?.chat === 'Y' && (
                                <div className="appointment-item">
                                  <div className="radio-select text-center">
                                    <input className="form-check-input ms-0 mt-0" name="service2" type="radio" id="service10"
                                      value="chat"
                                      checked={appointmentTypeInput === 'chat'}
                                      onChange={(e) => setAppointmentTypeInput(e.target.value)} />
                                    <label className="form-check-label" htmlFor="service10">
                                      <i className="isax isax-messages-15" />
                                      <span className="service-title d-block">Chat</span>
                                    </label>
                                  </div>
                                </div>)}
                              {data?.appointments?.homeVisit === 'Y' && (
                                <div className="appointment-item">
                                  <div className="radio-select text-center">
                                    <input className="form-check-input ms-0 mt-0" name="service2" type="radio" id="service11"
                                      value="homeVisit"
                                      checked={appointmentTypeInput === 'homeVisit'}
                                      onChange={(e) => setAppointmentTypeInput(e.target.value)} />
                                    <label className="form-check-label" htmlFor="service11">
                                      <i className="isax isax-messages-15" />
                                      <span className="service-title d-block">Home Visit</span>
                                    </label>
                                  </div>
                                </div>)}
                            </div>
                            <div className="clinics-path" style={{ display: "none" }}>
                              <h6 className="mb-3">Select Clinics</h6>
                              <div>
                                <div className="service-item">
                                  <input className="form-check-input ms-0 mt-0" name="service2" type="checkbox" id="service12" />
                                  <label className="form-check-label ms-2" htmlFor="service12">
                                    <span className="d-flex align-items-center flex-wrap rpw-gap-2">
                                      <span className="d-inline-block me-2">
                                        <img src={`${serverConfig.BASE_URL}/uploads/${data?.image}`}
                                          className="rounded-circle" style={{ width: "60px", height: "80px", objectFit: "cover" }} alt={"appointment"}
                                          onError={(e) => {
                                            e.target.onerror = null; // prevents infinite loop
                                            e.target.src = "assets/img/hospital.jpg";
                                          }} />
                                      </span>
                                      <span>
                                        <span className="service-title d-block mb-1">{data?.clinicName}</span>
                                        <span className="fs-14">{data?.location}</span>
                                      </span>
                                    </span>
                                  </label>
                                </div>
                              </div>
                            </div>


                          </div>
                        </div>
                      </div>
                      <div className="card-body booking-body">
                        <div className="card mb-0">
                          <div className="card-body pb-1">
                            <div className="row">
                              <div className="col-lg-5">
                                <div className="card">
                                  {/* Mobile Scroller View */}
                                  <div className="d-md-none">
                                    <div className="d-flex overflow-auto pb-2" style={{ gap: '10px', whiteSpace: 'nowrap', scrollbarWidth: 'none' }}>
                                      {mobileDates.map((date, idx) => {
                                        const isSelected = selectedDate.toDateString() === date.toDateString();
                                        return (
                                          <div
                                            key={idx}
                                            onClick={() => {
                                              setSelectedDate(date);
                                              // FIX: Store as string immediately
                                              setAppointmentDateInput(formatToISO(date));
                                            }}
                                            className={`text-center p-2 rounded-3 border ${isSelected ? 'bg-primary text-white' : 'bg-light'}`}
                                            style={{ minWidth: '60px', cursor: 'pointer' }}
                                          >
                                            <div style={{ fontSize: '12px' }}>{date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()}</div>
                                            <div className="fw-bold">{date.getDate()}</div>
                                            <div style={{ fontSize: '10px' }}>{date.toLocaleDateString('en-US', { month: 'short' })}</div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>

                                  {/* Desktop Calendar View */}
                                  <div className="d-none d-md-block">
                                    <div ref={pickerRef} />
                                  </div>
                                </div>
                              </div>
                              <div className="col-lg-7">
                                <div className="card booking-wizard-slots">
                                  <div className="card-body">
                                    {timeSlots.morning.length === 0 &&
                                      timeSlots.afternoon.length === 0 &&
                                      timeSlots.evening.length === 0 ? (
                                      <div className="text-center text-muted mt-3 mb-3">
                                        Sorry 😔 No available slots.
                                      </div>
                                    ) : (
                                      <>
                                        <div className="book-title">
                                          <h6 className="fs-14 mb-2">Morning</h6>
                                        </div>
                                        <div className="d-none d-md-flex flex-wrap token-slot mt-2 mb-2" style={{
                                          display: "flex",
                                          flexWrap: "wrap",
                                          overflowX: "auto",
                                          gap: "4px"
                                        }}>
                                          {timeSlots.morning.map((slot, i, arr) =>
                                            renderSlot(slot, arr[i + 1] || new Date(slot.getTime() + 15 * 60 * 1000))
                                          )}
                                        </div>
                                        {/* Mobile View: Custom Dropdown Style */}
                                        <div className="d-md-none mb-3">
                                          <div className="dropdown w-100">
                                            <button
                                              className="btn btn-outline-light w-100 text-start d-flex justify-content-between align-items-center dropdown-toggle-custom"
                                              type="button"
                                              data-bs-toggle="collapse"
                                              data-bs-target="#morningSlotsMobile"
                                              style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '12px' }}
                                            >
                                              <span className="text-dark fw-bold">Select Morning Slot</span>
                                              <i className="isax isax-arrow-down-1"></i>
                                            </button>
                                            <div className="collapse mt-1" id="morningSlotsMobile">
                                              <div className="appointment-scroll-mobile">
                                                {timeSlots.morning.map((slot, i, arr) =>
                                                  renderSlot(slot, arr[i + 1] || new Date(slot.getTime() + 15 * 60 * 1000))
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                        <div className="book-title">
                                          <h6 className="fs-14 mb-2">Afternoon</h6>
                                        </div>
                                        <div className="d-none d-md-flex flex-wrap token-slot mt-2 mb-2">
                                          {timeSlots.afternoon.map((slot, i, arr) =>
                                            renderSlot(slot, arr[i + 1] || new Date(slot.getTime() + 15 * 60 * 1000))
                                          )}
                                        </div>
                                        {/* Mobile View: Custom Dropdown Style */}
                                        <div className="d-md-none mb-3">
                                          <div className="dropdown w-100">
                                            <button
                                              className="btn btn-outline-light w-100 text-start d-flex justify-content-between align-items-center dropdown-toggle-custom"
                                              type="button"
                                              data-bs-toggle="collapse"
                                              data-bs-target="#afternoonSlotsMobile"
                                              style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '12px' }}
                                            >
                                              <span className="text-dark fw-bold">Select Afternoon Slot</span>
                                              <i className="isax isax-arrow-down-1"></i>
                                            </button>
                                            <div className="collapse mt-1" id="afternoonSlotsMobile">
                                              <div className="appointment-scroll-mobile">
                                                {timeSlots.afternoon.map((slot, i, arr) =>
                                                  renderSlot(slot, arr[i + 1] || new Date(slot.getTime() + 15 * 60 * 1000))
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        </div>


                                        <div className="book-title">
                                          <h6 className="fs-14 mb-2">Evening</h6>
                                        </div>
                                        <div className="d-none d-md-flex flex-wrap token-slot mt-2 mb-2">
                                          {timeSlots.evening.map((slot, i, arr) =>
                                            renderSlot(slot, arr[i + 1] || new Date(slot.getTime() + 15 * 60 * 1000))
                                          )}
                                        </div>
                                        {/* Mobile View: Custom Dropdown Style */}
                                        <div className="d-md-none mb-3">
                                          <div className="dropdown w-100">
                                            <button
                                              className="btn btn-outline-light w-100 text-start d-flex justify-content-between align-items-center dropdown-toggle-custom"
                                              type="button"
                                              data-bs-toggle="collapse"
                                              data-bs-target="#eveningSlotsMobile"
                                              style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '12px' }}
                                            >
                                              <span className="text-dark fw-bold">Select Morning Slot</span>
                                              <i className="isax isax-arrow-down-1"></i>
                                            </button>
                                            <div className="collapse mt-1" id="eveningSlotsMobile">
                                              <div className="appointment-scroll-mobile">
                                                {timeSlots.evening.map((slot, i, arr) =>
                                                  renderSlot(slot, arr[i + 1] || new Date(slot.getTime() + 15 * 60 * 1000))
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="card-body booking-body">
                        <div className="card mb-0">
                          <div className="card-body pb-1">
                            <div className="row">
                              <div className="col-sm-8 col-12">
                                <div className="payment-tabs">
                                  <select
                                    className="form-control pe-5"
                                    style={{
                                      appearance: "none",
                                      WebkitAppearance: "none",
                                      MozAppearance: "none",
                                      backgroundColor: "#e9ecef",
                                      border: "1px solid #ced4da",
                                      borderRadius: "0.375rem",
                                      cursor: "not-allowed",
                                    }}
                                    value={selectPatientFromList}
                                    onChange={(e) => {
                                      const patientId = e.target.value;
                                      setSelectPatientFromList(patientId);

                                      const patient = patientData?.find(p => Number(p.patientId) === Number(patientId));
                                      if (patient) {
                                        handleSelectPatient(patient);
                                        setShowForm(true);
                                        setIsAddNew(false);
                                      }
                                    }}
                                  >
                                    <option value="">Select Patient</option>

                                    {!isLoadingPat &&
                                      patientData?.map((patient) => (
                                        <option key={patient?.patientId} value={patient?.patientId}>
                                          {patient?.patientName} ({patient?.phone} - {patient?.age})
                                        </option>
                                      ))}
                                  </select>
                                </div>
                              </div>
                              <div className="col-sm-4 col-12">
                                <div class="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="checkDefault"
                                    checked={isAddNew}
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      setIsAddNew(checked);

                                      if (checked) {
                                        resetForm();
                                        setShowForm(true);
                                        setSelectPatientFromList("");
                                      } else {
                                        setShowForm(false);
                                      }
                                    }}
                                  />
                                  <label class="form-check-label" for="checkDefault">
                                    Add new +
                                  </label>
                                </div>
                              </div>
                            </div>
                            {showForm && (
                              <div className="row">
                                <div className="col-lg-4 col-md-6">
                                  <div className="mb-3 position-relative">
                                    <label className="form-label">Patient Phone<span style={{ color: "red" }}>* </span></label>
                                    <input
                                      type="text"
                                      className="form-control"
                                      pattern="\d{10}"
                                      title="Please enter exactly 10 digits"
                                      maxLength="10"
                                      minLength="10"
                                      required
                                      value={phone}
                                      onChange={(e) => {
                                        handleSearchChange(e);
                                        setErrors(prev => ({ ...prev, phone: null }));
                                      }}
                                      autoComplete="off"
                                      placeholder="7875446775"
                                    />
                                    {errors.phone && <span className="text-danger small">{errors.phone}</span>}
                                    {isPatientDropdownOpen && patientOptions.length > 0 && (
                                      <ul className="list-group position-absolute w-100 z-index-1050" style={{ maxHeight: '200px', overflowY: 'auto', top: '100%', left: 0, zIndex: 1050 }}>
                                        {patientOptions.map((patient) => (
                                          <li
                                            key={patient.patientId}
                                            className="list-group-item list-group-item-action cursor-pointer"
                                            onClick={() => handleSelectPatient(patient)}
                                          >
                                            <div>{patient.patientName} <small className="text-muted">{patient.phone}</small></div>
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                </div>
                                <div className="col-lg-4 col-md-6">
                                  <div className="mb-3">
                                    <label className="form-label">Full Name<span style={{ color: "red" }}>* </span></label>
                                    <input type="text" className="form-control"
                                      value={patientName}
                                      onChange={(e) => {
                                        setPatientName(e.target.value);
                                        setErrors(prev => ({ ...prev, patientName: null }));
                                      }}
                                      placeholder="Parush Kumar"
                                    />
                                    {errors.patientName && <span className="text-danger small">{errors.patientName}</span>}
                                  </div>
                                </div>

                                <div className="col-lg-4 col-md-6">
                                  <div className="mb-3">
                                    <label className="form-label">Gender<span style={{ color: "red" }}>* </span></label>
                                    <div className="position-relative">
                                      <select
                                        className="form-control pe-5"
                                        style={{
                                          appearance: 'none',
                                          WebkitAppearance: 'none',
                                          MozAppearance: 'none',
                                          border: '1px solid #ced4da',  // manually define border
                                          borderRadius: '0.375rem',     // matches Bootstrap default
                                        }}
                                        value={genderInput}
                                        onChange={(e) => {
                                          setGenderInput(e.target.value);
                                          setErrors(prev => ({ ...prev, genderInput: null }));
                                        }}
                                      >
                                        <option value="--select--">--Select--</option>
                                        <option value="MALE">Male</option>
                                        <option value="FEMALE">Female</option>
                                        <option value="OTHERS">Others</option>
                                      </select>
                                      <i
                                        className="fas fa-chevron-down position-absolute"
                                        style={{
                                          top: '50%',
                                          right: '15px',
                                          transform: 'translateY(-50%)',
                                          pointerEvents: 'none',
                                          color: '#6c757d',
                                        }}
                                      ></i>
                                    </div>
                                    {errors.genderInput && <span className="text-danger small">{errors.genderInput}</span>}
                                  </div>
                                </div>

                                <div className="col-lg-4 col-md-6">
  <div className="mb-3">
    <label className="form-label">Email Address</label>
    <input 
      type="email" 
      className="form-control"
      value={emailInput}
      onChange={(e) => {
        setEmailInput(e.target.value);
        setErrors(prev => ({ ...prev, emailInput: null }));   // Clear error on typing
      }}
      placeholder="example@email.com"
    />
    {errors.emailInput && <span className="text-danger small">{errors.emailInput}</span>}
  </div>
</div>
                                <div className="col-lg-4 col-md-6">
                                  <div className="mb-3">
                                    <label className="form-label">Age<span style={{ color: "red" }}>* </span></label>
                                    <input type="number" className="form-control"
                                      value={ageInput}
                                      onChange={(e) => {
                                        const age = e.target.value;
                                        setAgeInput(age);
                                        setErrors(prev => ({ ...prev, ageInput: null }));
                                        if (age) {
                                          const today = new Date();
                                          const birthYear = today.getFullYear() - parseInt(age);
                                          const dobDate = new Date(birthYear, today.getMonth(), today.getDate());
                                          setDob(dobDate.toISOString().split('T')[0]);
                                        } else {
                                          setDob('');
                                        }
                                      }}
                                      placeholder="30" />
                                    {errors.ageInput && <span className="text-danger small">{errors.ageInput}</span>}
                                  </div>
                                </div>

                                <div className="col-lg-4 col-md-6">
                                  <div className="mb-3">
                                    <label className="form-label">DOB</label>
                                    <input type="date" className="form-control"
                                      value={dob}
                                      onChange={(e) => {
                                        const dobValue = e.target.value;
                                        setDob(dobValue);
                                        if (dobValue) {
                                          const birthDate = new Date(dobValue);
                                          const today = new Date();
                                          let age = today.getFullYear() - birthDate.getFullYear();
                                          const m = today.getMonth() - birthDate.getMonth();
                                          if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                                            age--;
                                          }
                                          setAgeInput(age >= 0 ? age : 0);
                                          setErrors(prev => ({ ...prev, ageInput: null }));
                                        } else {
                                          setAgeInput('');
                                        }
                                      }}
                                      placeholder="12-01-2000" />
                                  </div>
                                </div>
                                <div className="col-lg-12 col-md-12">
                                  <div className="mb-3">
                                    <label className="form-label">Address<span style={{ color: "red" }}>* </span></label>
                                    <input type="text" className="form-control"
                                      value={address}
                                      onChange={(e) => {
                                        setAddress(e.target.value);
                                        setErrors(prev => ({ ...prev, address: null }));
                                      }}
                                      placeholder="Patna"
                                    />
                                    {errors.address && <span className="text-danger small">{errors.address}</span>}
                                  </div>
                                </div>
                                <div className="col-lg-12">
                                  <div className="mb-3">
                                    <label className="form-label">Reason For Visit<span style={{ color: "red" }}>* </span></label>
                                    <textarea className="form-control" rows={3} defaultValue={""}
                                      value={problemInput}
                                      onChange={(e) => {
                                        setProblemInput(e.target.value);
                                        setErrors(prev => ({ ...prev, problemInput: null }));
                                      }}
                                      placeholder="Write reason"
                                    />
                                    {errors.problemInput && <span className="text-danger small">{errors.problemInput}</span>}
                                  </div>
                                </div>
                                <div className="col-lg-4 col-md-6" style={{ display: "none" }}>
                                  <div className="mb-3">
                                    <label className="form-label">Pin Code</label>
                                    <input type="text" className="form-control"
                                      value={pincode}
                                      onChange={(e) => setPincode(e.target.value)}
                                      placeholder="pin code"
                                    />
                                  </div>
                                </div>
                                <div className="col-lg-4 col-md-6" style={{ display: "none" }}>
                                  <div className="mb-3">
                                    <label className="form-label">City</label>
                                    <input type="text" className="form-control"
                                      value={city}
                                      onChange={(e) => setCity(e.target.value)}
                                      placeholder="Patna" />
                                  </div>
                                </div>

                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="card-footer mt-4">
                        <div className="d-flex align-items-center justify-content-between">
                          {currentStep === 1 && (
                            <button className="btn btn-md btn-dark rounded-pill" onClick={(e) => prevStep(e, 2)}>
                              <i className="isax isax-arrow-left-2 me-1" />
                              Back
                            </button>
                          )}
                          {currentStep < 2 && (
                            <button className="btn btn-md btn-primary-gradient rounded-pill" onClick={
                              (e) => {
                                e.preventDefault();
                                if (validateForm()) {
                                  nextStep(e, 1);
                                } else {
                                  Swal.fire({
                                    icon: "warning",
                                    title: "Incomplete Form",
                                    text: "Please check the highlighted fields and correct the errors.",
                                  });
                                }
                              }}>
                              Select Payments
                              <i className="isax isax-arrow-right-3 ms-1" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}


                  {currentStep === 2 && (

                    <div className="card booking-card mb-0">
                      <div className="card-header">
                        <div className="booking-header pb-0">
                          <div className="card mb-0">
                            <div className="card-body">
                              <div className="d-flex align-items-center flex-wrap rpw-gap-2 flex-wrap row-gap-2">
                                <span className="avatar avatar-xxxl avatar-rounded me-2 flex-shrink-0"><img src={`${serverConfig.BASE_URL}/uploads/${data.image}`}
                                  onError={(e) => {
                                    e.target.onerror = null; // prevents infinite loop
                                    e.target.src = "assets/img/doctor.png";
                                  }}
                                  alt={"appointment"} /></span>
                                <div>
                                  <h4 className="mb-1">Dr. {data.doctorName} </h4>
                                  <p className="text-indigo mb-3 fw-medium">{data.designation}</p>
                                  <p className="mb-0"><i className="isax isax-location me-2" />{data.location}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="card-body booking-body">
                        {selectPayment === "NETBANKING" ? (
                          <div className="row">
                            <div className="col-lg-6 d-flex">
                              <div className="card flex-fill mb-3 mb-lg-0">
                                <div className="card-body">
                                  <h6 className="mb-3">Payment Gateway</h6>
                                  <div className="payment-tabs">
                                    <ul className="nav nav-pills mb-3 row" id="pills-tab" role="tablist">
                                      <li className="nav-item col-sm-4" role="presentation">
                                        <button className="nav-link active" id="pills-home-tab" data-bs-toggle="pill" data-bs-target="#pills-home" type="button" role="tab">
                                          <img src="assets/img/icons/payment-icon-05.svg" className="me-2" alt={"appointment"} />
                                          Credit Card
                                        </button>
                                      </li>
                                      <li className="nav-item col-sm-4" role="presentation">
                                        <button className="nav-link" id="pills-profile-tab" data-bs-toggle="pill" data-bs-target="#pills-profile" type="button" role="tab">
                                          <img src="assets/img/icons/payment-icon-06.svg" className="me-2" alt={"appointment"} />
                                          Paypal
                                        </button>
                                      </li>
                                      <li className="nav-item col-sm-4" role="presentation">
                                        <button className="nav-link" id="pills-contact-tab" data-bs-toggle="pill" data-bs-target="#pills-contact" type="button" role="tab">
                                          <img src="assets/img/icons/payment-icon-07.svg" className="me-2" alt={"appointment"} />
                                          Stripe
                                        </button>
                                      </li>
                                    </ul>
                                    <div className="tab-content" id="pills-tabContent">
                                      <div className="tab-pane fade show active" id="pills-home" role="tabpanel">
                                        <div className="mb-3">
                                          <label className="form-label">Card Holder Name</label>
                                          <div className="position-relative input-icon">
                                            <input type="text" className="form-control" />
                                            <span><i className="isax isax-user" /></span>
                                          </div>
                                        </div>
                                        <div className="mb-3">
                                          <label className="form-label">Card Number</label>
                                          <div className="position-relative input-icon">
                                            <input type="text" className="form-control" />
                                            <span><i className="isax isax-card-tick" /></span>
                                          </div>
                                        </div>
                                        <div className="mb-3">
                                          <label className="form-label">Expire Date</label>
                                          <div className="position-relative input-icon">
                                            <input type="text" className="form-control" />
                                            <span><i className="isax isax-calendar-2" /></span>
                                          </div>
                                        </div>
                                        <div className="mb-0">
                                          <label className="form-label">CVV</label>
                                          <div className="position-relative input-icon">
                                            <input type="text" className="form-control" />
                                            <span><i className="isax isax-check" /></span>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="tab-pane fade" id="pills-profile" role="tabpanel">
                                        <div className="mb-3">
                                          <label className="form-label">Email Address</label>
                                          <div className="position-relative input-icon">
                                            <input type="text" className="form-control" />
                                            <span><i className="isax isax-sms" /></span>
                                          </div>
                                        </div>
                                        <div>
                                          <label className="form-label">Password</label>
                                          <div className="pass-group">
                                            <input type="password" className="form-control pass-input" />
                                            <span className="feather-eye-off toggle-password" />
                                          </div>
                                        </div>
                                      </div>
                                      <div className="tab-pane fade" id="pills-contact" role="tabpanel">
                                        <div className="mb-3">
                                          <label className="form-label">Email Address</label>
                                          <div className="position-relative input-icon">
                                            <input type="text" className="form-control" />
                                            <span><i className="isax isax-sms" /></span>
                                          </div>
                                        </div>
                                        <div>
                                          <label className="form-label">Password</label>
                                          <div className="pass-group">
                                            <input type="password" className="form-control pass-input-sub" />
                                            <span className="feather-eye-off toggle-password-sub" />
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="col-lg-6 d-flex">
                              <div className="card flex-fill mb-0">
                                <div className="card-body">
                                  <h6 className="mb-3">Booking Info</h6>
                                  <div className="mb-3">
                                    <label className="form-label">Date &amp; Time</label>
                                    <div className="form-plain-text">{appointmentTimeInput && appointmentDateInput && (
                                      <>
                                        {appointmentTimeInput}, {formattedDate}
                                      </>
                                    )}</div>
                                  </div>
                                  <div className="mb-3">
                                    <label className="form-label">Appointment type</label>
                                    <div className="form-plain-text">{appointmentTypeInput} </div>
                                  </div>
                                  <div className="pt-3 border-top booking-more-info">
                                    <h6 className="mb-3">Payment Info</h6>
                                    <div className="d-flex align-items-center flex-wrap rpw-gap-2 justify-content-between mb-2">
                                      <p className="mb-0">Consultation Fee</p>
                                      <span className="fw-medium d-block">&#8377; {data.consultaionFee}</span>
                                    </div>
                                    <div className="d-flex align-items-center flex-wrap rpw-gap-2 justify-content-between mb-2">
                                      <p className="mb-0">Service Fees</p>
                                      <span className="fw-medium d-block">&#8377; 42.37</span>
                                    </div>
                                    <div className="d-flex align-items-center flex-wrap rpw-gap-2 justify-content-between mb-2">
                                      <p className="mb-0">Tax</p>
                                      <span className="fw-medium d-block">&#8377; 7.63</span>
                                    </div>

                                  </div>
                                  <div className="bg-primary d-flex align-items-center flex-wrap rpw-gap-2 justify-content-between p-3 rounded">
                                    <h6 className="text-white">Total</h6>
                                    <h6 className="text-white">&#8377; {Number(data.consultaionFee) + 50}</h6>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>) : (<div className="row">
                            <div className="col-lg-12 d-flex">
                              <div className="card flex-fill mb-3 mb-lg-0">
                                <div className="card-body">
                                  {/* <h6 className="mb-3">Payment Gateway</h6> */}
                                  <div className="payment-tabs">
                                    <select
                                      className="form-control pe-5"
                                      disabled
                                      style={{
                                        appearance: 'none',
                                        WebkitAppearance: 'none',
                                        MozAppearance: 'none',
                                        backgroundColor: '#e9ecef', // matches Bootstrap's disabled input
                                        border: '1px solid #ced4da',
                                        borderRadius: '0.375rem',
                                        cursor: 'not-allowed',
                                      }}
                                      value={selectPayment}
                                    >
                                      <option value={""}>On Clininc Payment</option>
                                      <option value={"NETBANKING"}>Payment Gateway</option>
                                    </select>
                                  </div>
                                </div>
                              </div>
                            </div>

                          </div>)}
                      </div>
                      <div className="card-footer mt-4">
                        <div className="d-flex align-items-center justify-content-between">
                          {currentStep > 1 && (
                            <button className="btn btn-md btn-dark rounded-pill" onClick={(e) => prevStep(e, 2)}>
                              <i className="isax isax-arrow-left-2 me-1" />
                              Back
                            </button>
                          )}
                          {currentStep < 3 && (
                            <button className="btn btn-md btn-primary-gradient rounded-pill" onClick={(e) => {
                              if (selectPayment) { nextStep(e, 2) } else {
                                e.preventDefault(); Swal.fire({
                                  icon: "warning",
                                  title: "Incomplete Form",
                                  text: "Please select the payment options first.",
                                });
                              }
                              handleBookingSubmit(e)
                            }}>{(selectPayment === "ONCLINICPAYMENT") ? "Book Appointment" :
                              "Confirm & Pay"}
                              <i className="isax isax-arrow-right-3 ms-1" />
                            </button>
                          )}
                        </div>
                      </div>

                    </div>

                  )}
                  {currentStep === 3 && (
                    <>
                      <div className="card booking-card">
                        <div className="card-body booking-body pb-1">
                          <div className="row">
                            <div className="col-lg-8 d-flex">
                              <div className="flex-fill">
                                <div className="card ">
                                  <div className="card-header">
                                    <h5 className="d-flex align-items-center flex-wrap rpw-gap-2">
                                      <i className="isax isax-tick-circle5 text-success me-2" />
                                      Booking Confirmed
                                    </h5>
                                  </div>
                                  <div className="card-header d-flex align-items-center flex-wrap rpw-gap-2 py-2">
                                    <span className="avatar avatar-lg avatar-rounded me-2 flex-shrink-0 "><img src={`${serverConfig.BASE_URL}/uploads/${data.image}`} alt={"appointment"}
                                      onError={(e) => {
                                        e.target.onerror = null; // prevents infinite loop
                                        e.target.src = "assets/img/doctor.png";
                                      }} /></span>
                                    <div className="doctor-info">
                                      <h4 className="mb-2">Dr. {data.doctorName}</h4>
                                      <h6 className="text-indigo mb-3 fw-medium">{data.designation}</h6>
                                      <p className="mb-3">
                                        <i className="isax isax-location me-2" />
                                        {data.location}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="card-body pb-1">
                                    <div className="d-flex align-items-center flex-wrap rpw-gap-2 justify-content-between mb-3">
                                      <h6>Booking Info</h6>
                                      {/* <a href="/" role="button" onClick={(e)=>{e.preventDefault()}} className="btn btn-light rounded-pill"><i className="isax isax-calendar me-1" />Reschedule</a>
                             */}
                                    </div>
                                    <div className="row">

                                      <div className="col-md-6">
                                        <div className="mb-3">
                                          <label className="form-label">Date &amp; Time</label>
                                          <div className="form-plain-text">{appointmentTimeInput && appointmentDateInput && (
                                            <>
                                              {appointmentTimeInput}, {formattedDate}
                                            </>)}</div>
                                        </div>
                                      </div>
                                      <div className="col-md-6">
                                        <div className="mb-3">
                                          <label className="form-label">Appointment type</label>
                                          <div className="form-plain-text">{(appointmentTypeInput === 'clinic')?'Clinic':
                                         (appointmentTypeInput === 'videoCall')? 'Video call':
                                         (appointmentTypeInput === 'audioCall')? 'Audio call':
                                         (appointmentTypeInput === 'chat')? 'Chat':
                                         (appointmentTypeInput === 'homeVisit')? 'Home visit':
                                         appointmentTypeInput} </div>
                                        </div>
                                      </div>
                                      <div className="col-md-6">
                                        <div className="mb-3">
                                          <label className="form-label">Clinic Name &amp; Location</label>
                                          <div className="form-plain-text">{data?.clinicName} <a href="/" role="button" className="text-primary" onClick={(e) => { e.preventDefault() }} >View Location</a></div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="card">
                                  <div className="card-body d-flex align-items-center flex-wrap rpw-gap-2 justify-content-between">
                                    <div>
                                      <h6 className="mb-1">Need Our Assistance</h6>
                                      <p className="mb-0">Call us in case you face any Issue on Booking / Cancellation</p>
                                    </div>
                                    <a href="/" role="button" onClick={(e) => { e.preventDefault() }} className="btn btn-light rounded-pill"><i className="isax isax-call5 me-1" />Call Us</a>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="col-lg-4 d-flex">
                              <div className="card flex-fill">
                                <div className="card-body d-flex flex-column justify-content-between">
                                  <div className="text-center">
                                    <h6 className="fs-14 mb-2">Booking Number</h6>
                                    <span className="booking-id-badge mb-3">{bookingId}</span>
                                    <h6 className="fs-14 mb-2">Patient Id</h6>
                                    <span className="booking-id-badge mb-3">{patientNumber}</span>
                                
                                    <p className="mb-0">Your appointment with Dr. {data.doctorName} has been confirmed. Please arrive at least 15 minutes prior to your scheduled time.</p>
                                  </div>
                                  <div>

                                    <a href="/search-doctors" className="btn w-100 btn-md btn-primary-gradient next_btns inline-flex align-items-center rounded-pill">
                                      Start New Booking
                                    </a>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div>
                        <a href="/patient-appointments" onClick={()=>{localStorage.setItem('titles','Appointments')}}>
                          <i className="isax isax-arrow-left-2 me-1"/>
                          Return to appointments
                        </a>
                      </div>
                    </>
                  )}
                </div>
                <div className="text-center">
                  <p className="mb-0">Copyright © {new Date().getFullYear()}. All Rights Reserved, Webnet Technologies</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* /Terms */}
      </div>)}
    </>
  )
}